import React, { useCallback, useContext } from 'react';
import { RadioButtonsType } from '../../../../components/forms/RadioButtons/RadioButtons.view';
import { PostTiming } from '../../../../models/graphql';
import { RadioButtons } from '../../../../components/forms/RadioButtons';
import { RegisterOptions, UseFormReturn, ValidateResult } from 'react-hook-form';
import { PostEditorValidationMessages } from '../../constants/validation-messages';
import { BoxNotification } from '../../../../components/containers/BoxNotification';
import { SetDateAndTime } from '../SetDateAndTime';
import { RepeatRegularly } from '../RepeatRegularly';
import { FormElementAction } from '../../../../models/basic-objects';
import { Tooltip } from '../../../../components/actions/Tooltip';
import { hasSeverity } from '../../validators/helpers/severity-checker';
import { ErrorMessageSeverity } from '../../../../constants/form';
import { getErrorMessages } from '../../validators/helpers/getErrorMessages';
import { ActiveCustomizeIndexContext } from '../../states/ActiveCustomizeIndexProvider';
import { PostEditorGroupDataContext } from '../../states/PostEditorGroupDataProvider';
import { PostEditorContext } from '../../states/PostEditorProvider';
import classes from './SchedulePost.module.scss';
import classNames from 'classnames';
import { formatErrorMessages } from '../../validators/helpers/formatErrorMessages';
import { getCustomizationProfileId } from '../../validators/helpers/customization-getter';
import { BeaconContext } from 'providers/BeaconProvider';
import scssVars from '../../../../assets/scss/exportedVars.module.scss';

export type SchedulePostViewProps = {
  //
  formInstance: UseFormReturn;
  loading: boolean;
  postTiming: PostTiming;
  postGroupName: string;
  action: FormElementAction;
  postNowDisabled: boolean;
  tooltip?: JSX.Element;
};

const isScheduling = (postTiming: PostTiming) => {
  switch (postTiming) {
    case PostTiming.NOW:
    case PostTiming.SCHEDULE:
    case PostTiming.RECURRING:
      return true;
    default:
      return false;
  }
};

export const SchedulePostView = ({
  action,
  loading,
  formInstance,
  postTiming,
  postGroupName,
  postNowDisabled,
  tooltip
}: SchedulePostViewProps): JSX.Element => {
  const { state: postEditorState } = useContext(PostEditorContext);
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const { postGroups } = postEditorState;
  const group = postGroups[postGroupIndex];
  const { errors } = group;
  const profileId = getCustomizationProfileId(group, customizeIndex);
  const { beacon } = useContext(BeaconContext);

  const variationErrorMessages = getErrorMessages(errors, profileId, undefined, [
    'pinterestDuplicatesCheck',
    'preventPostNowForDisconnected',
    'preventTwDuplicate',
    'scheduleTextOrMediaVariations'
  ]);

  const formattedVariationMessages = formatErrorMessages(variationErrorMessages);
  const hasPostNowWarnings =
    postTiming === PostTiming.NOW ? hasSeverity(errors, ErrorMessageSeverity.warning, profileId, undefined, []) : false;
  const hasPostNowErrors =
    postTiming === PostTiming.NOW
      ? hasSeverity(errors, ErrorMessageSeverity.error, profileId, undefined, [
          'pinterestDuplicatesCheck',
          'preventTwDuplicate',
          'preventPostNowForDisconnected',
          'scheduleTextOrMediaVariations'
        ])
      : false;

  const hasSchedulingWarnings =
    postTiming === PostTiming.SCHEDULE
      ? hasSeverity(errors, ErrorMessageSeverity.warning, profileId, undefined, [])
      : false;
  const hasSchedulingErrors =
    postTiming === PostTiming.SCHEDULE
      ? hasSeverity(errors, ErrorMessageSeverity.error, profileId, undefined, [
          'pinterestDuplicatesCheck',
          'preventTwDuplicate',
          'scheduleTextOrMediaVariations'
        ])
      : false;

  const hasRecurringWarnings =
    postTiming === PostTiming.RECURRING
      ? hasSeverity(errors, ErrorMessageSeverity.warning, profileId, undefined, [])
      : false;
  const hasRecurringErrors =
    postTiming === PostTiming.RECURRING
      ? hasSeverity(errors, ErrorMessageSeverity.error, profileId, undefined, [
          'pinterestDuplicatesCheck',
          'preventTwDuplicate',
          'scheduleTextOrMediaVariations'
        ])
      : false;
  const recurringErrorMessages = getErrorMessages(errors, profileId, undefined, [
    'pinterestDuplicatesCheck',
    'preventTwDuplicate',
    'scheduleTextOrMediaVariations'
  ]);
  const formattedrecurringErrorMessages = formatErrorMessages(recurringErrorMessages);

  const scheduleRadios: RadioButtonsType[] = [
    {
      value: PostTiming.NOW,
      disabled: postNowDisabled,
      label: (
        <>
          Post Now
          {(hasPostNowErrors || hasPostNowWarnings) && (
            <Tooltip
              className={classNames(classes.Tooltip, classes.TooltipRadio)}
              icon={hasPostNowErrors ? 'alert' : 'warning'}
              target={`#postNowTooltipError`}
              position={'right'}
              pointer={true}
              type={'inlined'}
              autoZIndex={false}
              style={{ zIndex: scssVars['z-index-overmodal'] }}>
              <>{formattedVariationMessages}</>
            </Tooltip>
          )}
        </>
      ),
      tooltip
    },
    {
      value: PostTiming.SCHEDULE,
      label: (
        <>
          Set Date & Time
          {(hasSchedulingErrors || hasSchedulingWarnings) && (
            <Tooltip
              className={classNames(classes.Tooltip, classes.TooltipRadio)}
              icon={hasSchedulingErrors ? 'alert' : 'warning'}
              target={`#scheduleTooltipError`}
              position={'right'}
              pointer={true}
              type={'inlined'}
              autoZIndex={false}
              style={{ zIndex: scssVars['z-index-overmodal'] }}>
              <>{formattedVariationMessages}</>
            </Tooltip>
          )}
        </>
      )
    },
    {
      value: PostTiming.RECURRING,
      label: (
        <>
          Recurring
          {(hasRecurringWarnings || hasRecurringErrors) && postTiming === PostTiming.RECURRING && (
            <Tooltip
              className={classes.Tooltip}
              icon={hasRecurringErrors ? 'alert' : 'warning'}
              target={`#hasDuplicateErrorTooltip`}
              position={'right'}
              pointer={true}
              type={'inlined'}
              autoZIndex={false}
              style={{ zIndex: scssVars['z-index-overmodal'] }}>
              <>{formattedrecurringErrorMessages}</>
            </Tooltip>
          )}
        </>
      )
    }
  ];

  const radioRules: RegisterOptions = {
    validate: (value): ValidateResult => {
      if (value === PostTiming.NOW && postNowDisabled) return undefined;
      if (!value) return PostEditorValidationMessages.schedule.type.required;
    }
  };

  const infoBox = useCallback((): JSX.Element => {

    switch (postTiming) {
      case PostTiming.NOW:
        return (
          <>
            <p className={'no-b-m'}>Publish this Post immediately.</p>
            <a onClick={() => beacon('article', '688', { type: 'modal' })}>Learn more</a>.
          </>
        );
      case PostTiming.SCHEDULE:
        return (
          <>
            <p className={'no-b-m'}>Manually schedule this Post for specific dates and times.</p>
            <a onClick={() => beacon('article', '688', { type: 'modal' })}>Learn more</a>.
          </>
        );
      case PostTiming.RECURRING:
        return (
          <>
            <p className={'no-b-m'}>Schedule this Post to repeat at regular intervals.</p>
            <a onClick={() => beacon('article', '688', { type: 'modal' })}>Learn more</a>.
          </>
        );
      default:
        return <></>;
    }
  }, [postTiming, beacon]);

  return (
    <>
      <div className={'margin-top-small-16'}>
        <RadioButtons
          disabled={loading}
          action={action}
          value={isScheduling(postTiming) ? postTiming : undefined}
          displayType={'block'}
          formInstance={formInstance}
          items={scheduleRadios}
          name={`${postGroupName}.postTiming`}
          rules={radioRules}
          size={'medium'}
        />
      </div>
      <div className={'margin-top-small-24'}>
        <BoxNotification type={'info'}>{infoBox()}</BoxNotification>
      </div>
      {postTiming === PostTiming.SCHEDULE && (
        <SetDateAndTime formInstance={formInstance} groupName={`${postGroupName}.schedule`} />
      )}
      {postTiming === PostTiming.RECURRING && (
        <RepeatRegularly formInstance={formInstance} groupName={`${postGroupName}.repeat`} />
      )}
    </>
  );
};
